import { useState } from 'react'
import { MdClose } from 'react-icons/md'
import {
  DEFAULT_PALLET_TYPE,
  DEFAULT_SERVICE_LEVEL,
  NETWORK_PALLET_TYPES,
  PALLET_UNIT,
  SERVICE_LEVELS,
  SHIPPING_UNITS,
  ServiceIdentity,
  getServiceLabel,
  isSameService,
} from './types'

interface Props {
  existingServices: ServiceIdentity[]
  onAdd: (service: ServiceIdentity) => void
  onClose: () => void
}

export function AddServiceModal({ existingServices, onAdd, onClose }: Props) {
  const [serviceName, setServiceName] = useState<string>(DEFAULT_SERVICE_LEVEL)
  const [unit, setUnit] = useState<string>(PALLET_UNIT)
  const [palletType, setPalletType] = useState<string>(DEFAULT_PALLET_TYPE)
  const [error, setError] = useState('')

  const buildService = (): ServiceIdentity => ({
    serviceName,
    unit,
    ...(unit === PALLET_UNIT ? { palletType } : {}),
  })

  const handleSubmit = () => {
    const service = buildService()
    if (existingServices.some((existing) => isSameService(existing, service))) {
      setError(`"${getServiceLabel(service)}" already exists`)
      return
    }
    onAdd(service)
    onClose()
  }

  const selectClass =
    'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'

  return (
    <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/50'>
      <div className='bg-white rounded-xl shadow-xl w-[400px] p-6'>
        <div className='flex items-center justify-between mb-4'>
          <h3 className='text-base font-semibold text-gray-900'>Add Service</h3>
          <button
            type='button'
            onClick={onClose}
            className='p-1 hover:bg-gray-100 rounded transition-colors'
          >
            <MdClose className='w-5 h-5 text-gray-500' />
          </button>
        </div>

        <div className='space-y-4'>
          <div>
            <label className='block text-sm font-medium text-gray-700 mb-1'>Service level</label>
            <select
              value={serviceName}
              onChange={(e) => {
                setServiceName(e.target.value)
                setError('')
              }}
              className={selectClass}
              autoFocus
            >
              {SERVICE_LEVELS.map((level) => (
                <option key={level} value={level}>
                  {level}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className='block text-sm font-medium text-gray-700 mb-1'>Unit</label>
            <select
              value={unit}
              onChange={(e) => {
                setUnit(e.target.value)
                setError('')
              }}
              className={selectClass}
            >
              {SHIPPING_UNITS.map((u) => (
                <option key={u.value} value={u.value}>
                  {u.label}
                </option>
              ))}
            </select>
          </div>

          {unit === PALLET_UNIT && (
            <div>
              <label className='block text-sm font-medium text-gray-700 mb-1'>Pallet type</label>
              <select
                value={palletType}
                onChange={(e) => {
                  setPalletType(e.target.value)
                  setError('')
                }}
                className={selectClass}
              >
                {NETWORK_PALLET_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.value}
                  </option>
                ))}
              </select>
            </div>
          )}

          {error && <p className='text-red-500 text-xs'>{error}</p>}
        </div>

        <div className='flex justify-end gap-3 mt-6'>
          <button
            type='button'
            onClick={onClose}
            className='px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors'
          >
            Cancel
          </button>
          <button
            type='button'
            onClick={handleSubmit}
            className='px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors'
          >
            Add Service
          </button>
        </div>
      </div>
    </div>
  )
}
